/**
 * Profile tab — photo + personal details.
 *
 * The photo is upload-only and saves immediately (its own endpoint); the details
 * are read-first and saved with an explicit Save. `onAvatarChange` / `onSaved` let
 * the page push the result straight into the store, so the sidebar updates with no
 * extra /auth/me round trip.
 */
import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Camera, Lock, Pencil, UserRound } from 'lucide-react';
import React, { useState } from 'react';

import AccountCard, { DetailRow } from '@/components/account/AccountCard';
import ImageUploader from '@/components/account/ImageUploader';
import apiService from '@/services/api.service';
import { prepareAvatarImage } from '@/utils/imageResize';

import { extractError, imageSizeProblem, imageTypeProblem, Notify, uploadedUrl } from './shared';

export interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  avatar: string;
}

interface Props {
  profile: ProfileData;
  onSaved: (patch: Pick<ProfileData, 'firstName' | 'lastName' | 'phoneNumber'>) => void;
  onAvatarChange: (url: string) => void;
  notify: Notify;
}

// Permissive on purpose (the backend doesn't format-check): digits, spaces and + ( ) . -
const PHONE_RE = /^[+()\d][\d\s().-]{4,19}$/;

const ProfilePanel: React.FC<Props> = ({ profile, onSaved, onAvatarChange, notify }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ firstName: '', lastName: '', phoneNumber: '' });
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoBusy, setPhotoBusy] = useState<'uploading' | 'removing' | null>(null);

  const initials =
    `${profile.firstName.charAt(0)}${profile.lastName.charAt(0)}`.toUpperCase() || '?';

  // ── photo ──────────────────────────────────────────────────────────────────
  const pickPhoto = async (picked: File) => {
    // Type on the original; size only AFTER resizing (a big photo is fine — it gets shrunk).
    const typeProblem = imageTypeProblem(picked);
    if (typeProblem) return notify(typeProblem, 'error');
    setPhotoBusy('uploading');
    try {
      const file = await prepareAvatarImage(picked);
      const sizeProblem = imageSizeProblem(file);
      if (sizeProblem) return notify(sizeProblem, 'error');
      const form = new FormData();
      form.append('avatar', file);
      // axios defaults to application/json — override so the multipart boundary is set (see api.service.ts).
      const res: any = await apiService.post('/auth/me/avatar', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onAvatarChange(uploadedUrl(res, (d) => d?.avatar));
      notify('Photo updated', 'success');
    } catch (error: any) {
      notify(extractError(error, 'Failed to upload photo'), 'error');
    } finally {
      setPhotoBusy(null);
    }
  };

  const removePhoto = async () => {
    setPhotoBusy('removing');
    try {
      await apiService.delete('/auth/me/avatar');
      onAvatarChange('');
      notify('Photo removed', 'success');
    } catch (error: any) {
      notify(extractError(error, 'Failed to remove photo'), 'error');
    } finally {
      setPhotoBusy(null);
    }
  };

  // ── details ────────────────────────────────────────────────────────────────
  const startEdit = () => {
    setDraft({
      firstName: profile.firstName,
      lastName: profile.lastName,
      phoneNumber: profile.phoneNumber,
    });
    setTouched(false);
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setTouched(false);
  };

  const trimmed = {
    firstName: draft.firstName.trim(),
    lastName: draft.lastName.trim(),
    phoneNumber: draft.phoneNumber.trim(),
  };
  const errors = {
    firstName: trimmed.firstName ? '' : 'First name is required',
    lastName: trimmed.lastName ? '' : 'Last name is required',
    phoneNumber:
      !trimmed.phoneNumber || PHONE_RE.test(trimmed.phoneNumber)
        ? ''
        : 'Enter a valid phone number',
  };
  const invalid = Boolean(errors.firstName || errors.lastName || errors.phoneNumber);
  const dirty =
    trimmed.firstName !== profile.firstName ||
    trimmed.lastName !== profile.lastName ||
    trimmed.phoneNumber !== profile.phoneNumber;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (invalid || !dirty || saving) return;
    setSaving(true);
    try {
      await apiService.patch('/auth/me', trimmed);
      onSaved(trimmed);
      setEditing(false);
      notify('Profile updated', 'success');
    } catch (error: any) {
      notify(extractError(error, 'Failed to update profile'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const show = (field: keyof typeof errors) => touched && Boolean(errors[field]);

  return (
    <Grid container spacing={3} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <AccountCard
          icon={<Camera size={18} strokeWidth={1.75} />}
          title="Profile photo"
          subtitle="Shown next to your name across the workspace."
        >
          <ImageUploader
            {...(profile.avatar ? { src: profile.avatar } : {})}
            fallback={initials}
            shape="circle"
            size={88}
            busy={photoBusy}
            uploadLabel="Upload photo"
            noun="profile photo"
            hint="PNG, JPG or WebP. Big photos are cropped to a square and shrunk for you."
            onPick={pickPhoto}
            onRemove={removePhoto}
          />
        </AccountCard>
      </Grid>

      <Grid item xs={12} md={7}>
        <AccountCard
          icon={<UserRound size={18} strokeWidth={1.75} />}
          title="Personal details"
          subtitle="Your name and contact info."
          {...(editing
            ? {
                onSubmit: save,
                onEscape: cancel,
                footer: (
                  <>
                    <Button
                      onClick={cancel}
                      disabled={saving}
                      sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving || !dirty}
                      sx={{ textTransform: 'none', fontWeight: 600, minWidth: 120 }}
                    >
                      {saving ? (
                        <CircularProgress size={18} sx={{ color: 'inherit' }} />
                      ) : (
                        'Save changes'
                      )}
                    </Button>
                  </>
                ),
              }
            : {
                action: (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={startEdit}
                    startIcon={<Pencil size={15} strokeWidth={1.75} />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderColor: 'divider',
                      color: 'text.primary',
                    }}
                  >
                    Edit
                  </Button>
                ),
              })}
        >
          {editing ? (
            <Stack spacing={2.25}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  label="First name"
                  value={draft.firstName}
                  onChange={(e) => setDraft((d) => ({ ...d, firstName: e.target.value }))}
                  autoComplete="given-name"
                  autoFocus
                  fullWidth
                  required
                  error={show('firstName')}
                  helperText={show('firstName') ? errors.firstName : ' '}
                />
                <TextField
                  label="Last name"
                  value={draft.lastName}
                  onChange={(e) => setDraft((d) => ({ ...d, lastName: e.target.value }))}
                  autoComplete="family-name"
                  fullWidth
                  required
                  error={show('lastName')}
                  helperText={show('lastName') ? errors.lastName : ' '}
                />
              </Stack>
              <TextField
                label="Email"
                value={profile.email}
                disabled
                fullWidth
                helperText="Managed by your administrator — it can't be changed here."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Lock size={16} strokeWidth={1.75} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label="Phone number"
                value={draft.phoneNumber}
                onChange={(e) => setDraft((d) => ({ ...d, phoneNumber: e.target.value }))}
                autoComplete="tel"
                placeholder="+91 98765 43210"
                fullWidth
                error={show('phoneNumber')}
                helperText={show('phoneNumber') ? errors.phoneNumber : 'Optional'}
              />
            </Stack>
          ) : (
            <>
              <DetailRow label="First name">{profile.firstName}</DetailRow>
              <DetailRow label="Last name">{profile.lastName}</DetailRow>
              <DetailRow
                label="Email"
                hint={
                  <Typography
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: 'inherit',
                    }}
                  >
                    <Lock size={12} strokeWidth={1.75} /> Managed by your administrator
                  </Typography>
                }
              >
                {profile.email}
              </DetailRow>
              <DetailRow label="Phone number">{profile.phoneNumber}</DetailRow>
            </>
          )}
        </AccountCard>
      </Grid>
    </Grid>
  );
};

export default ProfilePanel;
